import PF from "pathfinding";
import { ObstacleStrategies } from "./ObstacleStrategies";
import { ConnectingLineHandler } from "../ConnectingLineHandler";
import { getPossibleEndpointPositions } from "../../components/cell/grid-cell.utils";
import { getInstanceIDFromCanvasInstance } from "../../../../diagram/diagram-assets/diagram-positioning-state";

/**
 * @description Pathfinding handler
 */
export class PathfindingHandler extends ObstacleStrategies {
    MATRIX_WIDTH;

    MATRIX_HEIGHT;

    finder;

    from;

    to;

    path;

    initialPoints;

    connectingLineHandler;

    matrix;

    strategies;

    linesToIgnore;

    turnPenalty = 10;

    constructor(grid, fromCell, toCell, linePoints, linesToIgnore) {
        super(grid, getInstanceIDFromCanvasInstance(fromCell.ctx));
        this.from = fromCell;
        this.to = toCell;
        this.initialPoints = linePoints ? this.formatInitialTwoPoints(linePoints) : linePoints;
        this.connectingLineHandler = new ConnectingLineHandler(this.instanceId); // instanceId from parent class
        this.linesToIgnore = linesToIgnore || [];
        this.strategies = this.getStrategies();
    }

    /**
     * @description performs pathfinding search between two elements
     */
    search() {
        this.MATRIX_WIDTH = this.grid[0].length * this.BREAK_FACTOR;
        this.MATRIX_HEIGHT = this.grid.length * this.BREAK_FACTOR;
        // TODO: Possible improvements:
        // Dont use the entire grid for the matrix. Use a mini-grid formed from the connection of the two cells plus 3-4-5 extra cells all around.
        // Increase grid if path not found until reaching maximum grid.
        const startContainer = this.from.getContainerInstance();
        const startPoints = this.getPointExpandedCell(getPossibleEndpointPositions(startContainer, true, this.from));
        const endContainer = this.to.getContainerInstance();
        const endPoints = this.getPointExpandedCell(getPossibleEndpointPositions(endContainer, true, this.to));
        if (this.initialPoints) {
            const initialTwoPoints = this.searchInitialTwoPoints(this.initialPoints);
            if (initialTwoPoints) return initialTwoPoints;
        }
        if (this.initialPoints) {
            const startPointToAnyPoint = this.searchStartToAny(this.initialPoints.start, endPoints);
            if (startPointToAnyPoint) return startPointToAnyPoint;
        }
        const searchAnyToAny = this.searchAnyToAny(startPoints, endPoints);
        if (searchAnyToAny) return searchAnyToAny;

        return [];
    }

    /**
     * @description Performs the search  between the initial two points
     */
    searchInitialTwoPoints(points) {
        this.createMatrix();
        this.initializeFinder();
        let path = null;
        for (let i = 0; i < this.strategies.length; i++) {
            const matrix = this.setObstacles(this.matrix.clone(), points.start, points.end, this.strategies[i], this.linesToIgnore);
            path = this.getPossibleEndpointPath(points.start[0], points.end[0], matrix);
            if (path && path.length > 0) return this.processResult({ path, endpointSide: points.end[0].side });
        }
        return path;
    }

    /**
     * @description performs the search between the start endpoint and any endpoint on the target
     */
    searchStartToAny(start, endpoints) {
        this.createMatrix();
        this.initializeFinder();
        const matrix = this.setObstacles(this.matrix, [start], endpoints, null, this.linesToIgnore);
    }

    /**
     * @description performs the search between any start endpoitn to any end endpoint
     */
    searchAnyToAny(startpoints, endpoints) {
        this.createMatrix();
        this.initializeFinder();
        const matrix = this.setObstacles(this.matrix, startpoints, endpoints, null, this.setObstacles);
        const paths = [];
        for (let x = 0; x < startpoints.length; x++) {
            for (let i = 0; i < endpoints.length; i++) {
                const matrixClone = matrix.clone();
                const path = this.getPossibleEndpointPath(startpoints[x], endpoints[i], matrixClone);
                paths.push({
                    path,
                    length: PF.Util.pathLength(path),
                    endpointSide: endpoints[i].side
                });
            }
        }
        const result = paths.filter((path) => path.length !== 0).reduce((a, b) => {
            const smallest = Math.min(a.length, b.length);
            return smallest === a.length ? a : b;
        });
        return this.processResult(result);
    }

    /**
     * @description Initializez the pathfinding class, AStarFinder algorithm
     */
    initializeFinder() {
        this.finder = new PF.AStarFinder({
            diagonalMovement: PF.DiagonalMovement.Never,
            weight: 2,
            avoidStaircase: true,
            turnPenalty: this.turnPenalty
        });
    }

    /**
     * @description Processes the provided result  by turning minified grid into real canvas pixel sizes and compresses th epath
     * @param {Object} result
     */
    processResult(result) {
        result.path = PF.Util.compressPath(result.path);
        return this.turnMinifiedGridIntoPixels(result);
    }

    /**
     * @description turns minified grid into pixels
     * @param {*} searchResult
     */
    turnMinifiedGridIntoPixels(searchResult) {
        const connectingLine = { path: searchResult.path, side: searchResult.endpointSide };
        for (let i = 0; i < connectingLine.path.length; i++) {
            connectingLine.path[i] = {
                x: (connectingLine.path[i][0] * this.BREAK_FACTOR) + this.gridConfig.viewPadding,
                y: (connectingLine.path[i][1] * this.BREAK_FACTOR) + this.gridConfig.viewPadding
            };
        }
        return connectingLine;
    }

    /**
     * @description searches the provided matrix for a path starting from start to end
     */
    getPossibleEndpointPath(start, endpoint, matrix) {
        const finder = new PF.AStarFinder({
            diagonalMovement: PF.DiagonalMovement.Never,
            weight: 2,
            avoidStaircase: true,
            turnPenalty: this.turnPenalty
        });
        const path = finder.findPath(start.col, start.row, endpoint.col, endpoint.row, matrix);
        return path;
    }

    /**
     * @description gets the row and col of the cell from the expanded cells array provided
     * @param {*} points
     */
    getPointExpandedCell(points) {
        const keys = Object.keys(points);
        const returnVal = [];
        keys.forEach((point) => {
            returnVal.push({
                col: Math.floor((points[point].x - this.gridConfig.viewPadding) / this.BREAK_FACTOR),
                row: Math.floor((points[point].y - this.gridConfig.viewPadding) / this.BREAK_FACTOR),
                side: point
            });
        });
        return returnVal;
    }

    /**
     * @description creates the matrix used for the pathfinding algorithm
     */
    createMatrix() {
        this.matrix = new PF.Grid(this.MATRIX_WIDTH, this.MATRIX_HEIGHT);
    }

    /**
     * @description formats the initial two points into a suitable format for the operations perfomed here
     * @param {*} points
     */
    formatInitialTwoPoints(points) {
        points.start = this.getPointExpandedCell(points.start);
        points.end = this.getPointExpandedCell(points.end);
        return points;
    }
}
